import { revalidateLogic } from "@tanstack/react-form";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute, redirect, useRouter } from "@tanstack/react-router";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { z } from "zod";
import logo from "@/assets/landing/logo-rocortado.png";
import { useAppForm } from "@/components/form/tanstack-form";
import { Button } from "@/components/ui/button";
import { InputOTP, InputOTPGroup, InputOTPSeparator, InputOTPSlot } from "@/components/ui/input-otp";
import { useLogout } from "@/hooks/use-logout";
import { parsePocketBaseError } from "@/lib/errors/pocketbase";
import { pb } from "@/lib/pocketbase";

const validateSearch = z.object({
	phoneNumber: z.string().min(1, "El número de teléfono es obligatorio"),
});

export const Route = createFileRoute("/auth/verify-phone")({
	validateSearch,
	beforeLoad: () => {
		if (!pb.authStore.isValid) {
			throw redirect({ to: "/auth/login" });
		}
	},
	component: RouteComponent,
});

const OTP_LENGTH = 6;

const formSchema = z.object({
	otp: z.string().length(OTP_LENGTH, "El código debe tener 6 dígitos"),
});

type FormValues = z.infer<typeof formSchema>;

function RouteComponent() {
	const router = useRouter();
	const logout = useLogout();
	const { t } = useTranslation();
	const phoneNumber = Route.useSearch({
		select: (search) => search.phoneNumber,
	});

	const verifyPhone = useMutation({
		mutationFn: async (data: FormValues) => {
			toast.loading(
				t(($) => $.modules.auth.verifyPhone.status.loading.title),
				{
					id: "verify-phone",
					description: t(($) => $.modules.auth.verifyPhone.status.loading.message),
				}
			);

			await pb.send("/api/verify-code", {
				method: "POST",
				body: { phoneNumber, verificationCode: data.otp },
			});

			return data;
		},
		onSuccess: async () => {
			toast.success(
				t(($) => $.modules.auth.verifyPhone.status.success.title),
				{
					id: "verify-phone",
					description: t(($) => $.modules.auth.verifyPhone.status.success.message),
				}
			);

			await router.navigate({ to: "/dashboard" });
		},
		onError: (error) => {
			const parsed = parsePocketBaseError(error, defaultValues);

			if (parsed.unknown) {
				return toast.error(
					t(($) => $.modules.auth.verifyPhone.status.error.title),
					{
						id: "verify-phone",
						description: parsed.message || t(($) => $.modules.auth.verifyPhone.status.error.message),
					}
				);
			}

			form.setErrorMap({ onDynamic: parsed.errorMap });
			toast.dismiss("verify-phone");
		},
	});

	const resendCode = useMutation({
		mutationFn: async () => {
			toast.loading(
				t(($) => $.modules.auth.resendCode.status.loading.title),
				{
					id: "resend-code",
					description: t(($) => $.modules.auth.resendCode.status.loading.message),
				}
			);

			await pb.send("/api/send-verification-code-sms", {
				method: "POST",
				body: { phoneNumber },
			});

			return { phoneNumber };
		},
		onSuccess: () => {
			toast.success(
				t(($) => $.modules.auth.resendCode.status.success.title),
				{
					id: "resend-code",
					description: t(($) => $.modules.auth.resendCode.status.success.message),
				}
			);
		},
		onError: (error) => {
			const parsed = parsePocketBaseError(error, {});

			if (parsed.unknown) {
				return toast.error(
					t(($) => $.modules.auth.resendCode.status.error.title),
					{
						id: "resend-code",
						description: parsed.message || t(($) => $.modules.auth.resendCode.status.error.message),
					}
				);
			}

			toast.dismiss("resend-code");
		},
	});

	const defaultValues: FormValues = {
		otp: "",
	};

	const form = useAppForm({
		defaultValues,
		validationLogic: revalidateLogic({
			mode: "submit",
			modeAfterSubmission: "change",
		}),
		validators: { onDynamic: formSchema },
		onSubmit: ({ value }) => {
			verifyPhone.mutate(value);
		},
	});

	const handleSubmit = useCallback(
		(e: React.SubmitEvent) => {
			e.preventDefault();
			e.stopPropagation();
			form.handleSubmit();
		},
		[form]
	);

	return (
		<div className="mx-auto w-full max-w-md space-y-8">
			<div className="flex flex-col items-center">
				<img alt="Logo" height={"192.667"} src={logo} width={"447.967"} />
				{/* TODO: Add a better description */}
				<h2 className="mt-6 text-center font-bold text-3xl text-foreground tracking-tight">
					{t(($) => $.modules.auth.verifyPhone.title)}
				</h2>
			</div>

			<form.AppForm>
				<form className="space-y-6" onSubmit={handleSubmit}>
					<form.AppField name="otp">
						{(field) => (
							<field.Field>
								<field.FieldLabel>{t(($) => $.modules.auth.verifyPhone.fields.otp.label)}</field.FieldLabel>
								<field.FieldControl>
									<InputOTP
										className="w-full"
										maxLength={OTP_LENGTH}
										onBlurCapture={field.handleBlur}
										onChange={(value) => field.handleChange(value)}
										value={field.state.value}
									>
										<InputOTPGroup>
											<InputOTPSlot className="size-17" index={0} />
											<InputOTPSlot className="size-17" index={1} />
											<InputOTPSlot className="size-17" index={2} />
										</InputOTPGroup>
										<InputOTPSeparator />
										<InputOTPGroup>
											<InputOTPSlot className="size-17" index={3} />
											<InputOTPSlot className="size-17" index={4} />
											<InputOTPSlot className="size-17" index={5} />
										</InputOTPGroup>
									</InputOTP>
								</field.FieldControl>
								<field.FieldError />
							</field.Field>
						)}
					</form.AppField>
				</form>
			</form.AppForm>

			<Button
				className="mt-6 w-full"
				disabled={verifyPhone.isPending || resendCode.isPending}
				onClick={() => form.handleSubmit()}
			>
				{t(($) => $.modules.auth.verifyPhone.buttons.submit)}
			</Button>

			<Button
				className="w-full"
				disabled={resendCode.isPending || verifyPhone.isPending}
				onClick={() => resendCode.mutate()}
				variant="outline"
			>
				{t(($) => $.modules.auth.verifyPhone.buttons.resendCode)}
			</Button>

			<Button className="mt-4 w-full" disabled={logout.isPending} onClick={() => logout.mutate()} variant="secondary">
				{t(($) => $.modules.auth.verifyPhone.buttons.logout)}
			</Button>
		</div>
	);
}
